// Daily sector rundown: the SHAPE sector deity's spoken staff-meeting readout of where a sector
// stands today (POST /api/ai/sector-brief). The server caches one per user + sector + deity + day
// and writes each rundown into the deity's memory, so tomorrow's can say "since yesterday...".
//
// Client-side, calls are deduped per sector + deity + local day: the UI prefetches when the menu
// opens and asks again on pick, and both get the same promise (in flight or settled). A failed
// request is dropped from the cache so the next call retries.
import { apiFetch } from '../lib/api';
import type { Sector } from '../domain/olmps';
import { localDateKey } from './screenContext';

export interface SectorBrief {
  text: string;
  cached: boolean;
}

const inflight = new Map<string, Promise<SectorBrief>>();

export function fetchSectorBrief(
  sector: Sector,
  deityId: string,
  opts?: { refresh?: boolean },
): Promise<SectorBrief> {
  const today = localDateKey();
  const id = deityId.toLowerCase();
  const key = `${sector}:${id}:${today}`;
  const refresh = opts?.refresh === true;
  if (!refresh) {
    const existing = inflight.get(key);
    if (existing) return existing;
  }
  const p = apiFetch<{ text: string; cached: boolean; generatedAt?: string }>('/api/ai/sector-brief', {
    method: 'POST',
    body: JSON.stringify({ sector, deityId: id, today, refresh }),
  }).then((r) => ({ text: r.text, cached: r.cached }));
  inflight.set(key, p);
  p.catch(() => {
    if (inflight.get(key) === p) inflight.delete(key);
  });
  return p;
}

/** Forget cached rundowns (e.g. on sign-out). */
export function clearSectorBriefCache(): void {
  inflight.clear();
}
